
import React, { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import {
  BarChart,
  LineChart,
  Calendar,
  Clock,
  Award,
  Target,
  ArrowUp,
  ArrowDown,
  ArrowRight,
  Plus,
} from "lucide-react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  BarChart as RechartsBarChart,
  Bar,
} from "recharts";

// Sample data for charts
const performanceData = [
  { month: "Jan", earnings: 2400, jobs: 5, rating: 4.8 },
  { month: "Feb", earnings: 1398, jobs: 4, rating: 4.7 },
  { month: "Mar", earnings: 9800, jobs: 8, rating: 4.9 },
  { month: "Apr", earnings: 3908, jobs: 6, rating: 5.0 },
  { month: "May", earnings: 4800, jobs: 7, rating: 4.9 },
  { month: "Jun", earnings: 3800, jobs: 5, rating: 4.8 },
  { month: "Jul", earnings: 4300, jobs: 6, rating: 4.9 },
];

const goalsData = [
  { name: "Monthly Earnings", progress: 78, target: "$10,000", current: "$7,800" },
  { name: "Client Retention", progress: 92, target: "90%", current: "92%" },
  { name: "Hours Worked", progress: 65, target: "160 hrs", current: "104 hrs" },
  { name: "New Skills", progress: 40, target: "5", current: "2" },
];

const skillsData = [
  { name: "React", level: 85 },
  { name: "Node.js", level: 78 },
  { name: "GraphQL", level: 65 },
  { name: "TypeScript", level: 90 },
  { name: "UX Design", level: 72 },
];

const Performance = () => {
  const [activeTab, setActiveTab] = useState("overview");
  const [period, setPeriod] = useState("monthly");

  return (
    <div className="container mx-auto p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Performance Dashboard</h1>
          <p className="text-gray-500 mt-1">Track your freelancing metrics and growth</p>
        </div>
        <div className="flex gap-2 mt-4 sm:mt-0">
          <Button variant="outline" size="sm" className="flex items-center gap-1">
            <Calendar className="w-4 h-4" /> Last 30 Days
          </Button>
          <Button size="sm" className="bg-gradient-to-r from-purple-600 to-indigo-600">
            <Plus className="w-4 h-4 mr-1" /> Add Goal
          </Button>
        </div>
      </div>

      <Tabs defaultValue="overview" value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-muted/50 p-1">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="earnings">Earnings</TabsTrigger>
          <TabsTrigger value="projects">Projects</TabsTrigger>
          <TabsTrigger value="skills">Skills</TabsTrigger>
          <TabsTrigger value="goals">Goals</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { title: "Total Earnings", value: "$24,568", change: "+12%", icon: <BarChart className="w-4 h-4" />, color: "emerald" },
              { title: "Project Completion", value: "94%", change: "+3%", icon: <Target className="w-4 h-4" />, color: "sky" },
              { title: "Avg. Rating", value: "4.9/5", change: "+0.2", icon: <Award className="w-4 h-4" />, color: "amber" },
              { title: "Hours Worked", value: "168h", change: "-4%", icon: <Clock className="w-4 h-4" />, color: "purple" },
            ].map((stat, index) => (
              <Card key={index}>
                <CardContent className="p-4 sm:p-6">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-sm font-medium text-gray-500">{stat.title}</p>
                      <h3 className="text-2xl font-bold mt-1">{stat.value}</h3>
                      <div className={`flex items-center mt-1 text-${stat.color}-600 text-sm font-medium`}>
                        {stat.change.startsWith("+") ? (
                          <ArrowUp className="w-4 h-4 mr-1" />
                        ) : (
                          <ArrowDown className="w-4 h-4 mr-1" />
                        )}
                        {stat.change} this month
                      </div>
                    </div>
                    <div className={`p-2 rounded-full bg-${stat.color}-100`}>
                      {stat.icon}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Earnings Chart */}
          <Card>
            <CardHeader>
              <CardTitle>Earnings Overview</CardTitle>
              <CardDescription>Your earnings trend over time</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart
                    data={performanceData}
                    margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                  >
                    <defs>
                      <linearGradient id="colorEarnings" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#8884d8" stopOpacity={0.8} />
                        <stop offset="95%" stopColor="#8884d8" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="month" />
                    <YAxis />
                    <CartesianGrid strokeDasharray="3 3" />
                    <Tooltip />
                    <Area
                      type="monotone"
                      dataKey="earnings"
                      stroke="#8884d8"
                      fillOpacity={1}
                      fill="url(#colorEarnings)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Goals Overview */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle>Active Goals</CardTitle>
                <CardDescription>Your progress towards set targets</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {goalsData.slice(0, 3).map((goal, index) => (
                    <div key={index}>
                      <div className="flex justify-between mb-1">
                        <span className="text-sm font-medium">{goal.name}</span>
                        <span className="text-sm text-gray-500">
                          {goal.current} / {goal.target}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Progress value={goal.progress} className="h-2" />
                        <span className="text-xs font-medium">{goal.progress}%</span>
                      </div>
                    </div>
                  ))}
                </div>
                <Button variant="ghost" size="sm" className="mt-4 w-full">
                  View All Goals <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Top Skills</CardTitle>
                <CardDescription>Your highest-rated abilities</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {skillsData.slice(0, 3).map((skill, index) => (
                    <div key={index}>
                      <div className="flex justify-between mb-1">
                        <span className="text-sm font-medium">{skill.name}</span>
                        <span className="text-sm text-gray-500">{skill.level}/100</span>
                      </div>
                      <Progress value={skill.level} className="h-2" />
                    </div>
                  ))}
                </div>
                <Button variant="ghost" size="sm" className="mt-4 w-full">
                  View All Skills <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="earnings" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Earnings Analytics</CardTitle>
                  <CardDescription>Detailed view of your income</CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setPeriod("weekly")} 
                    className={period === "weekly" ? "bg-primary text-white" : ""}>
                    Weekly
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setPeriod("monthly")}
                    className={period === "monthly" ? "bg-primary text-white" : ""}>
                    Monthly
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setPeriod("yearly")}
                    className={period === "yearly" ? "bg-primary text-white" : ""}>
                    Yearly
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <RechartsBarChart
                    data={performanceData}
                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="earnings" name="Earnings ($)" fill="#8884d8" />
                    <Bar dataKey="jobs" name="Jobs Completed" fill="#82ca9d" />
                  </RechartsBarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle>Avg. Per Project</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">$1,240</div>
                <p className="text-sm text-gray-500 mt-1">15% increase from last month</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle>Daily Rate</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">$45.00</div>
                <p className="text-sm text-gray-500 mt-1">$5 increase from last quarter</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle>Revenue Share</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">8.5%</div>
                <p className="text-sm text-gray-500 mt-1">From platform partnership</p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="projects" className="space-y-4">
          {/* Projects content */}
          <Card>
            <CardHeader>
              <CardTitle>Project Performance</CardTitle>
              <CardDescription>Metrics for your recent projects</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Project list would go here */}
                <div className="text-center py-8">
                  <p className="text-muted-foreground">Projects section content coming soon</p>
                  <Button className="mt-4" size="sm">
                    <Plus className="mr-2 h-4 w-4" /> Add New Project
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="skills" className="space-y-4">
          {/* Skills content */}
          <Card>
            <CardHeader>
              <CardTitle>Skills Assessment</CardTitle>
              <CardDescription>Your skills and competency levels</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {skillsData.map((skill, index) => (
                  <div key={index}>
                    <div className="flex justify-between mb-1">
                      <span className="font-medium">{skill.name}</span>
                      <span className="text-gray-500">{skill.level}/100</span>
                    </div>
                    <Progress value={skill.level} className="h-3" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="goals" className="space-y-4">
          {/* Goals content */}
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Personal Goals</CardTitle>
                  <CardDescription>Track your professional targets</CardDescription>
                </div>
                <Button size="sm">
                  <Plus className="mr-2 h-4 w-4" /> New Goal
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {goalsData.map((goal, index) => (
                  <div key={index}>
                    <div className="flex justify-between mb-1">
                      <span className="font-medium">{goal.name}</span>
                      <span className="text-gray-500">
                        {goal.current} / {goal.target}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Progress value={goal.progress} className="h-3" />
                      <span className="font-medium min-w-[45px] text-right">{goal.progress}%</span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Performance;
